import clsx from 'clsx'
import React from 'react'
import { Logger } from 'wdc-cube'
import { bindUpdate, ViewSlot } from 'wdc-cube-react'

import { makeStyles } from 'tss-react/mui'

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'

import {
    Collapse,
    Divider,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from '@mui/material'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import { classToFComponent, FCClassContext } from 'src/utils/views'
import { ResultadoAnaliseRow, ResultadoRegraRow } from '../../bordero.types'
import ResultadoAnaliseScope from '../resultado_analise.scopes'

const LOG = Logger.get('RESULTADO-ANALISE-VIEW')

const useStyles = makeStyles()({
    view: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        padding: 10,
        minHeight: 600,
        rowGap: 10
    },
    cnpjInput: {
        flexGrow: 1
    },
    switch: {
        zoom: 0.6
    },
    hbox: {
        display: 'flex',
        flexDirection: 'row'
    },
    vbox: {
        display: 'flex',
        flexDirection: 'column'
    },
    syncButton: {
        alignSelf: 'center'
    }
})

type ResultadoAnaliseViewProps = {
    className?: string
    scope: ResultadoAnaliseScope
}
class ResultadoAnaliseViewClass implements FCClassContext<ResultadoAnaliseViewProps> {
    // :: Fields
    scope!: ResultadoAnaliseScope
    sytles!: ReturnType<typeof useStyles>

    // :: emissors
    readonly emitOnClose = () => {
        this.scope.onClose().catch(LOG.caught)
    }

    // :: Methods

    onSyncState({ scope }: ResultadoAnaliseViewProps) {
        this.scope = scope
        this.sytles = useStyles()
    }

    render({ className, scope }: ResultadoAnaliseViewProps) {
        this.scope = bindUpdate(React, scope)

        const classes = this.sytles.classes

        if (scope.waiting) {
            return <ViewSlot scope={scope.waiting} />
        }

        return (
            <div className={clsx(classes.view, className)}>
                <Paper elevation={3} style={{ padding: 10, minWidth: 700, height: 600 }}>
                    <TableContainer component={Paper}>
                        <Table size="small" aria-label="collapsible table">
                            <TableBody>
                                {Object.entries(this.scope.resultados).map(([categoria, itens]) => (
                                    <CategoryRow key={categoria} category={categoria} rows={itens} />
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Divider />

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'baseline',
                            gap: '10px',
                            marginTop: '10px'
                        }}
                    >
                        <Button variant="outlined" onClick={this.emitOnClose}>
                            Fechar
                        </Button>
                    </Box>
                </Paper>
            </div>
        )
    }
}

export const ResultadoAnaliseView = classToFComponent(ResultadoAnaliseViewClass, React)
export default ResultadoAnaliseView

function CategoryRow(props: { category: string; rows: ResultadoAnaliseRow[] }) {
    const { category, rows } = props
    const [open, setOpen] = React.useState(false)

    return (
        <React.Fragment>
            <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                <TableCell>
                    <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell
                    component="th"
                    scope="row"
                    sx={
                        category === 'BLOQUEAR'
                            ? {
                                  color: (theme) => theme.palette.error.dark,
                                  fontWeight: 'bold'
                              }
                            : category === 'ALERTAR'
                              ? {
                                    color: (theme) => theme.palette.warning.dark,
                                    fontWeight: 'bold'
                                }
                              : category === 'LIBERAR'
                                ? {
                                      color: (theme) => theme.palette.info.dark,
                                      fontWeight: 'bold'
                                  }
                                : category === 'SUCESSO'
                                  ? {
                                        color: (theme) => theme.palette.success.dark,
                                        fontWeight: 'bold'
                                    }
                                  : {}
                    }
                >
                    {category}
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1 }}>
                            <Table size="small" aria-label="collapsible table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell></TableCell>
                                        <TableCell>Análise</TableCell>
                                        <TableCell align="right">Rating 1</TableCell>
                                        <TableCell align="right">Rating 2</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.map((resultRow) => (
                                        <AnaliseResultRow
                                            key={resultRow.analiseId}
                                            category={category}
                                            row={resultRow}
                                        />
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </React.Fragment>
    )
}

function AnaliseResultRow(props: { category: string; row: ResultadoAnaliseRow }) {
    const { category, row } = props
    const [open, setOpen] = React.useState(false)

    return (
        <React.Fragment>
            <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                <TableCell>
                    <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell component="th" scope="row">
                    {row.analiseNome}
                </TableCell>
                <TableCell align="right">
                    {category !== 'SUCESSO' ? '-' : row.regras.map((r) => r.rating1).reduce((r1, r2) => r1 + r2)}
                </TableCell>
                <TableCell align="right">
                    {category !== 'SUCESSO' ? '-' : row.regras.map((r) => r.rating2).reduce((r1, r2) => r1 + r2)}
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell></TableCell>
                                        <TableCell>Regra</TableCell>
                                        <TableCell align="right">Rating 1</TableCell>
                                        <TableCell align="right">Rating 2</TableCell>
                                        <TableCell>Resultado</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {row.regras.map((regraRow) => (
                                        <RegraResultRow key={regraRow.id} category={category} regraRow={regraRow} />
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </React.Fragment>
    )
}

function RegraResultRow(props: { category: string; regraRow: ResultadoRegraRow }) {
    const { category, regraRow } = props
    const [open, setOpen] = React.useState(false)

    return (
        <React.Fragment key={regraRow.id}>
            <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                <TableCell>
                    <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell>{regraRow.nome}</TableCell>
                <TableCell align="right">{category === 'SUCESSO' ? regraRow.rating1 : '-'}</TableCell>
                <TableCell align="right">{category === 'SUCESSO' ? regraRow.rating2 : '-'}</TableCell>
                <TableCell>{regraRow.resultado}</TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Referência</TableCell>
                                        <TableCell>Resultado</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {regraRow.resultados.map((resultado) => (
                                        <TableRow key={resultado.id}>
                                            <TableCell>{resultado.referencia}</TableCell>
                                            <TableCell>{resultado.resultado}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </React.Fragment>
    )
}
